import { LocationFilterValue } from '@components/InputLocationFilter';

export type SearchContext = 'GLOBAL' | 'FAVORITES' | 'JOB_OFFER';
export type JobOfferScoringState = 'EMPTY' | 'SCORED';
export type ResumeReadinessStatus = 'READY' | 'CV_NOT_ANALYZED' | 'CV_MISSING';

export type TalentSortField =
  | 'RELEVANCE'
  | 'NAME'
  | 'EXPERIENCE'
  | 'SALARY'
  | 'RATING'
  | 'DISTANCE'
  | 'CREATED_AT'
  | 'CITY'
  | 'TOTAL_APPLICATIONS'
  | 'JOB_OFFER_SCORING';
export type SortOrder = 'ASC' | 'DESC';
export type FavoritePriority = 'LOW' | 'MEDIUM' | 'HIGH';
export type PresetVisibility = 'PRIVATE' | 'SHARED';

export interface RangeValue {
  min: number | null;
  max: number | null;
}

export interface LanguageFilterValue {
  languageId: string;
  minLevelId?: string;
}

export interface ITalentSearchFilter {
  jobOfferScoringStates?: JobOfferScoringState[];
  resumeReadinessStatuses?: ResumeReadinessStatus[];
  jobOfferScoringRange?: RangeValue;

  // Text
  query?: string;

  // Professional
  professionIds?: string[];
  specialtyIds?: string[];
  softSkillIds?: string[];
  certificateIds?: string[];
  diplomaTypes?: string[];

  // Experience & Salary
  experienceRange?: RangeValue;
  expectedSalaryRange?: RangeValue;

  // Location
  geoLocation?: LocationFilterValue | null;
  cities?: string[];
  countries?: string[];

  // Availability & Status
  availability?: string[];
  currentStatus?: string[];
  contractTypes?: string[];

  // Languages
  languages?: LanguageFilterValue[];

  // Favorites-only
  minRating?: number | null;
  tags?: string[];
  priority?: FavoritePriority[];
}

export type SearchPresetFilters = object;

export const defaultSearchFilters: ITalentSearchFilter = {
  jobOfferScoringStates: [],
  resumeReadinessStatuses: [],
  jobOfferScoringRange: { min: null, max: null },
  query: '',
  professionIds: [],
  specialtyIds: [],
  softSkillIds: [],
  certificateIds: [],
  diplomaTypes: [],
  experienceRange: { min: null, max: null },
  expectedSalaryRange: { min: null, max: null },
  geoLocation: null,
  cities: [],
  countries: [],
  availability: [],
  currentStatus: [],
  contractTypes: [],
  languages: [],
  minRating: null,
  tags: [],
  priority: [],
};

export interface SearchPreset<TFilters = ITalentSearchFilter> {
  _id: string;
  name: string;
  description?: string;
  filters: TFilters;
  visibility: PresetVisibility;
  createdBy: { _id: string; role: string };
  updatedBy?: { _id: string; role: string };
  createdAt: string;
  updatedAt: string;
}

export interface TalentSearchItem {
  talent: any; // Uses existing Talent type from the codebase
  distance?: number;
  favorite?: any; // Uses existing TalentFavorite type
}

export interface TalentSearchResult {
  docs: TalentSearchItem[];
  meta: {
    totalDocs: number;
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
    limit: number;
    pagingCounter: number;
  };
}

export interface ITalentSearchFilterOption {
  value: string;
  label: string;
  count: number;
  groupLabel?: string;
}

export interface ITalentSearchAvailableFilters {
  professionIds: ITalentSearchFilterOption[];
  specialtyIds: ITalentSearchFilterOption[];
  softSkillIds: ITalentSearchFilterOption[];
  certificateIds: ITalentSearchFilterOption[];
  diplomaTypes: ITalentSearchFilterOption[];
  cities: ITalentSearchFilterOption[];
  countries: ITalentSearchFilterOption[];
  availability: ITalentSearchFilterOption[];
  currentStatus: ITalentSearchFilterOption[];
  contractTypes: ITalentSearchFilterOption[];
  languageIds: ITalentSearchFilterOption[];
  languageLevelIds: ITalentSearchFilterOption[];
  tags: ITalentSearchFilterOption[];
  priority: ITalentSearchFilterOption[];
  jobOfferScoringStates: ITalentSearchFilterOption[];
  resumeReadinessStatuses: ITalentSearchFilterOption[];
  jobOfferScoringRange: RangeValue;
  experienceRange: RangeValue;
  expectedSalaryRange: RangeValue;
  maxRating: number | null;
}

export const defaultAvailableFilters: ITalentSearchAvailableFilters = {
  professionIds: [],
  specialtyIds: [],
  softSkillIds: [],
  certificateIds: [],
  diplomaTypes: [],
  cities: [],
  countries: [],
  availability: [],
  currentStatus: [],
  contractTypes: [],
  languageIds: [],
  languageLevelIds: [],
  tags: [],
  priority: [],
  jobOfferScoringStates: [],
  resumeReadinessStatuses: [],
  jobOfferScoringRange: { min: null, max: null },
  experienceRange: { min: null, max: null },
  expectedSalaryRange: { min: null, max: null },
  maxRating: null,
};
